import { cn } from "@/lib/utils";

interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: "sm" | "md" | "lg" | "xl";
  /** Optional className for custom styling */
  className?: string;
  /** Show full screen overlay */
  fullScreen?: boolean;
  /** Loading text to display */
  text?: string;
}

const sizeClasses = {
  sm: "h-4 w-4 border-2",
  md: "h-8 w-8 border-2",
  lg: "h-12 w-12 border-3",
  xl: "h-16 w-16 border-4",
};

const textSizeClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
  xl: "text-lg",
};

export function LoadingSpinner({
  size = "md",
  className,
  fullScreen = false,
  text,
}: LoadingSpinnerProps) {
  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const spinner = (
    <div className={cn("flex flex-col items-center justify-center gap-3", className)}>
      <div
        className={cn(
          "rounded-full border-primary/30 border-t-primary",
          sizeClasses[size],
          prefersReducedMotion ? "animate-none opacity-70" : "animate-spin"
        )}
        role="status"
        aria-label={text || "Carregando..."}
      />
      {text && (
        <p
          className={cn(
            "text-muted-foreground font-medium",
            textSizeClasses[size],
            !prefersReducedMotion && "animate-pulse"
          )}
        >
          {text}
        </p>
      )}
      <span className="sr-only">{text || "Carregando..."}</span>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        {spinner}
      </div>
    );
  }

  return spinner;
}

/** Inline loading spinner for buttons and small areas */
export function InlineSpinner({ className }: { className?: string }) {
  return (
    <LoadingSpinner size="sm" className={cn("inline-flex", className)} />
  );
}

/** Page-level loading state */
export function PageLoader({ text = "A carregar..." }: { text?: string }) {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <LoadingSpinner size="lg" text={text} />
    </div>
  );
}

/** Full screen loading overlay */
export function FullScreenLoader({ text = "A carregar..." }: { text?: string }) {
  return <LoadingSpinner size="xl" fullScreen text={text} />;
}

export default LoadingSpinner;
